import { type InterestAssetMatchScore } from '@aikoa/common'
import { useTranslations, type TFunction } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { LinearGradient } from 'expo-linear-gradient'
import { useMemo, useState, type PropsWithChildren } from 'react'
import { Platform, Pressable, StyleSheet, View } from 'react-native'
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated'
import FlowNavigation from '~/components/FlowNavigation'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import { InterestMatchBadge } from '~/components/interests/InterestMatchBadge'
import {
  AIKOA_FEE_PERCENT,
  CommissionSplitBar,
  LEAD_OWNER_FEE_PERCENT,
} from '~/components/leads/CommissionSplitBar'
import { LeadOfferedAsset } from '~/components/leads/LeadOfferedAsset'
import { validateLeadMatchScores } from '~/components/leads/utils'
import { ScrollView } from '~/components/ScrollView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { InfoAlert } from '~/components/ui/info-alert'
import { ConfettiTrigger } from '~/lib/ConfettiProvider'
import { trpc } from '~/lib/trpc'
import { cn } from '~/lib/utils'
import { useLeadStore } from './leadStore'

const isWeb = Platform.OS === 'web'

type LeadContactRequestSheetProps = {
  leadId: string
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  rejectedAssetIds?: string[]
}

type FlowStep = 'property' | 'pricing' | 'success'

function AccentSectionContainer({
  children,
  tone = 'surface',
  className,
  contentClassName,
}: PropsWithChildren<{
  tone?: 'surface' | 'muted' | 'accent'
  className?: string
  contentClassName?: string
}>) {
  return (
    <View
      className={cn(
        'mx-auto w-full max-w-lg',
        tone !== 'surface' && 'overflow-hidden rounded-2xl',
        tone === 'muted'
          ? 'bg-muted'
          : tone === 'accent'
            ? 'bg-tertiary-foreground/50'
            : 'bg-background',
        className
      )}>
      <View className={cn('gap-4 px-4 py-5', tone === 'surface' && 'p-0', contentClassName)}>
        {children}
      </View>
    </View>
  )
}

function PricingStepContent() {
  const { t } = useTranslations('leads')
  const { agreementAccepted, setAgreementAccepted } = useLeadStore()

  return (
    <Animated.View
      entering={FadeInDown.duration(150)}
      exiting={isWeb ? undefined : FadeOutDown.duration(150)}
      className="flex-1 gap-8 pb-6">
      <View className="items-center gap-2">
        <Label className="text-xs uppercase text-foreground">
          {t(($) => $.pricing.step2Label)}
        </Label>
        <Heading2 className="text-center text-2xl">{t(($) => $.pricing.step2Title)}</Heading2>
      </View>

      <AccentSectionContainer
        // eslint-disable-next-line i18next/no-literal-string
        tone="muted"
        contentClassName="gap-6 bg-muted/50">
        <View className="gap-3">
          <Heading3 className="text-center font-semibold text-lg">
            {t(($) => $.requestContact.commissionDescription)}
          </Heading3>
          <Body className="text-center text-base/normal text-foreground">
            {t(($) => $.requestContact.commissionNote)}
          </Body>
        </View>

        <CommissionSplitBar />
      </AccentSectionContainer>

      <Pressable
        onPress={() => setAgreementAccepted(!agreementAccepted)}
        className="flex-row items-start gap-4 px-2">
        <Checkbox
          checked={agreementAccepted}
          onCheckedChange={setAgreementAccepted}
          className="mt-0.5"
        />
        <Body className="flex-1 text-sm/relaxed text-foreground">
          {t(($) => $.requestContact.agreement, {
            aikoaPercent: AIKOA_FEE_PERCENT,
            leadOwnerPercent: LEAD_OWNER_FEE_PERCENT,
            totalPercent: AIKOA_FEE_PERCENT + LEAD_OWNER_FEE_PERCENT,
          })}
        </Body>
      </Pressable>
    </Animated.View>
  )
}

function PropertyStepContent({
  leadId,
  leadMatchScores,
  rejectedAssetIds,
}: {
  leadId: string
  leadMatchScores?: InterestAssetMatchScore
  rejectedAssetIds?: string[]
}) {
  const { t } = useTranslations('leads')

  const leadMatchIsValid = useMemo(
    () => validateLeadMatchScores(leadMatchScores),
    [leadMatchScores]
  )

  return (
    <Animated.View
      entering={FadeInDown.duration(150)}
      exiting={FadeOutDown.duration(150)}
      className="flex-1 gap-8">
      <View className="items-center gap-2">
        <Label className="text-xs uppercase text-foreground">
          {t(($) => $.pricing.step1Label)}
        </Label>
        <Heading2 className="text-center text-2xl">{t(($) => $.pricing.step1Title)}</Heading2>
      </View>
      <LeadOfferedAsset
        leadId={leadId}
        size="default"
        allowReset={false}
        rejectedAssetIds={rejectedAssetIds}
      />
      {!!leadMatchScores && (
        <View className="gap-4">
          <View className="flex-row items-center justify-center gap-3">
            <Label className="text-sm text-primary">{t(($) => $.detail.matchCompatibility)}</Label>
            <InterestMatchBadge scores={leadMatchScores} />
          </View>
          {!leadMatchIsValid ? (
            <InfoAlert level="warning" className="mx-4 w-auto">
              <Body>{t(($) => $.requestContact.matchNotValidWarning)}</Body>
            </InfoAlert>
          ) : (
            <View className="flex-row items-center justify-center gap-3">
              <Icon name="checkmark-circle" size={24} className="text-secondary" />
              <Body className="font-medium text-base text-foreground">
                {t(($) => $.requestContact.matchValidLabel)}
              </Body>
            </View>
          )}
        </View>
      )}
      <AccentSectionContainer
        // eslint-disable-next-line i18next/no-literal-string
        tone="muted"
        contentClassName="gap-3">
        <Label className="font-semibold text-xs uppercase tracking-widest">
          {t(($) => $.requestContact.nextStep)}
        </Label>
        <Body className="text-base/normal">{t(($) => $.requestContact.nextStepDescription)}</Body>
      </AccentSectionContainer>
    </Animated.View>
  )
}

function LeadContactRequestSuccessStep({
  onClose,
  t,
}: {
  onClose: () => void
  t: TFunction<'leads'>
}) {
  return (
    <Animated.View
      entering={FadeInDown.duration(150)}
      exiting={FadeOutDown.duration(150)}
      className="min-h-[300px] flex-1 items-center gap-8 px-6 pb-4 pt-12">
      <ConfettiTrigger
        confettiKey="lead-contact-success"
        timeout={400}
        className="size-[85px] items-center self-center">
        <Icon name="checkmark-circle" size={85} className="text-secondary" />
      </ConfettiTrigger>

      <View className="items-center gap-4">
        <Heading2 className="text-center text-2xl">{t(($) => $.requestContact.success)}</Heading2>
        <Body className="max-w-sm text-center text-foreground/70">
          {t(($) => $.requestContact.successDescription)}
        </Body>
      </View>

      <Button size="lg" variant="primary" onPress={onClose}>
        <ButtonLabel>{t(($) => $.requestContact.close)}</ButtonLabel>
      </Button>
    </Animated.View>
  )
}

function FloatingFooter({ children }: PropsWithChildren) {
  return (
    <View className="gap-3 px-4 pt-4">
      <LinearGradient
        pointerEvents="none"
        style={StyleSheet.absoluteFill}
        colors={['transparent', 'white']}
        locations={[0, 0.5]}
      />
      {children}
    </View>
  )
}

export function LeadContactRequestSheet({
  leadId,
  isOpen,
  onOpenChange,
  rejectedAssetIds,
}: LeadContactRequestSheetProps) {
  const { t } = useTranslations('leads')
  const [step, setStep] = useState<FlowStep>('property')

  const { showAlert } = useAlert()
  const { offerAssetId, agreementAccepted, reset: resetStore } = useLeadStore()

  const requestContactMutation = useMutation(trpc.leads.requestContact.mutationOptions())

  const handleConfirm = async () => {
    if (!offerAssetId) {
      return
    }

    try {
      const result = await requestContactMutation.mutateAsync({
        leadId,
        assetId: offerAssetId,
      })

      if ('error' in result && result.error) {
        const errorMessages: Record<string, string> = {
          LEAD_NOT_FOUND: t(($) => $.requestContact.errors.leadNotFound),
          LEAD_IS_OWN: t(($) => $.requestContact.errors.leadIsOwn),
          CONTACT_NOT_FOUND: t(($) => $.requestContact.errors.contactNotFound),
          ASSET_NOT_FOUND: t(($) => $.requestContact.errors.assetNotFound),
          ALREADY_REQUESTED: t(($) => $.requestContact.errors.alreadyRequested),
          NO_CONTACT_CHANNEL: t(($) => $.requestContact.errors.noContactChannel),
          NOTIFICATION_FAILED: t(($) => $.requestContact.errors.notificationFailed),
          GENERAL_ERROR: t(($) => $.requestContact.errors.generalError),
        }

        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description:
            errorMessages[result.error] ?? t(($) => $.requestContact.errors.generalError),
        })
        return
      }

      // eslint-disable-next-line i18next/no-literal-string
      setStep('success')
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.requestContact.errors.generalError),
      })
    }
  }

  const handleClose = () => {
    onOpenChange(false)

    setTimeout(() => {
      setStep('property')
      resetStore()
    }, 300)
  }

  const { data: lead } = useQuery(
    trpc.leads.getAssetLeadMatch.queryOptions(
      { assetId: offerAssetId ?? '', leadId },
      {
        enabled: !!offerAssetId,
      }
    )
  )

  const leadMatchIsValid = useMemo(() => validateLeadMatchScores(lead?.matchScores), [lead])

  const continueLabel = t(($) => $.requestContact.requestButton)
  const hasSelectedProperty = !!offerAssetId
  const isLoading = requestContactMutation.isPending
  const canProceedFromPricing = agreementAccepted

  return (
    <BottomSheet
      modalKey="lead-contact-request"
      isOpen={isOpen}
      onOpenChange={handleClose}
      showHandle={step !== 'success'}
      closeButtonClassName="left-3"
      showCloseButton={step !== 'success'}>
      {step === 'success' ? (
        <LeadContactRequestSuccessStep onClose={handleClose} t={t} />
      ) : (
        <ScrollView
          contentContainerClassName="gap-6 px-4 pb-0 pt-8"
          footerClassName="py-0 bottom-6!"
          className="max-h-[80vh] min-h-[350px] flex-1 sm:max-h-[75vh]"
          offsetTrackHeight
          footer={
            <FloatingFooter>
              <FlowNavigation
                className="native:relative web:relative border-t-0 p-0"
                onPrevious={step === 'pricing' ? () => setStep('property') : undefined}
                onNext={
                  // eslint-disable-next-line i18next/no-literal-string
                  step === 'property' ? () => setStep('pricing') : handleConfirm
                }
                nextLabel={
                  step === 'pricing' ? continueLabel : t(($) => $.requestContact.nextButton)
                }
                nextDisabled={
                  (step === 'property' && (!hasSelectedProperty || !leadMatchIsValid)) ||
                  (step === 'pricing' && !canProceedFromPricing) ||
                  isLoading
                }
                showBackground={false}
              />
            </FloatingFooter>
          }>
          {step === 'property' && (
            <PropertyStepContent
              leadId={leadId}
              leadMatchScores={lead?.matchScores}
              rejectedAssetIds={rejectedAssetIds}
            />
          )}
          {step === 'pricing' && <PricingStepContent />}
        </ScrollView>
      )}
    </BottomSheet>
  )
}

export default LeadContactRequestSheet
